import androidx.compose.runtime.*
import api.GraphQLClient
import api.TradeNewsApiClient
import components.BackendStatusSnapshot
import components.BackendStatusState
import components.CategoryFilter
import components.NewsDetail
import components.NewsList
import components.buildBackendStatusMessage
import components.buildBackendStatusSnapshot
import components.buildBackendStatusTitle
import components.buildBackendStatusTone
import components.buildBackendUnavailableMessage
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import msw.isMswEnabled
import navigation.Route
import navigation.parseRoute
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event

/** URLに?debugが含まれるかチェック */
fun isDebugMode(): Boolean = window.location.search.contains("debug")

@Composable
fun App(apiClient: TradeNewsApiClient? = null) {
    var selectedCategory by remember { mutableStateOf<String?>(null) }
    var backendStatus by remember {
        mutableStateOf(BackendStatusSnapshot(BackendStatusState.CHECKING))
    }
    var latestNewsError by remember { mutableStateOf<Throwable?>(null) }
    var healthCheckNonce by remember { mutableStateOf(0) }
    var route by remember { mutableStateOf(parseRoute(window.location.hash)) }
    val client = apiClient ?: remember { GraphQLClient() }

    DisposableEffect(client) {
        onDispose { client.close() }
    }

    // URLハッシュと画面状態を同期（ブラウザの戻る/進むにも追従）
    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            route = parseRoute(window.location.hash)
        }
        window.addEventListener("hashchange", listener)
        onDispose { window.removeEventListener("hashchange", listener) }
    }

    LaunchedEffect(healthCheckNonce) {
        backendStatus = BackendStatusSnapshot(BackendStatusState.CHECKING)

        backendStatus = try {
            buildBackendStatusSnapshot(client.fetchHealthStatus())
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            BackendStatusSnapshot(
                state = BackendStatusState.UNAVAILABLE,
                detail = buildBackendUnavailableMessage(e)
            )
        }
    }

    Style(AppStylesheet)
    Style(components.NewsCardStyles)
    Style(components.NewsListStyles)
    Style(components.NewsDetailStyles)
    Style(components.CategoryFilterStyles)

    Div(attrs = {
        classes("app-container")
    }) {
        Header(attrs = {
            classes("app-header")
        }) {
            H1 {
                Text("NBA ISO FLOW")
            }
            P(attrs = {
                classes("app-subtitle")
            }) {
                Text("NBAの最新ニュースをリアルタイムでお届け")
            }

            // バックエンドステータス: 異常時は常に表示、正常時は?debugモードのみ
            val showStatus = backendStatus.state != BackendStatusState.HEALTHY || latestNewsError != null || isDebugMode()
            if (showStatus) {
                Div(attrs = {
                    classes("backend-status", "backend-status-${buildBackendStatusTone(backendStatus, latestNewsError)}")
                }) {
                    Div(attrs = {
                        classes("backend-status-copy")
                    }) {
                        P(attrs = {
                            classes("backend-status-label")
                        }) {
                            Text(buildBackendStatusTitle(backendStatus, latestNewsError))
                        }
                        P(attrs = {
                            classes("backend-status-message")
                        }) {
                            Text(buildBackendStatusMessage(backendStatus, latestNewsError))
                        }
                    }
                    if (backendStatus.state != BackendStatusState.HEALTHY || latestNewsError != null) {
                        Button(attrs = {
                            classes("backend-status-button")
                            onClick { healthCheckNonce += 1 }
                        }) {
                            Text("接続を再確認")
                        }
                    }
                }
            }
        }

        // MSW 起動成功時のみインジケーターを表示（window.__mswStarted フラグで判定）
        val mswStarted = js("window.__mswStarted === true") as Boolean
        if (mswStarted) {
            Div(attrs = {
                classes("msw-banner")
            }) {
                Text("MSW Mode — API responses are mocked by Mock Service Worker")
            }
            Div(attrs = {
                classes("msw-indicator")
            }) {
                Text("MSW")
            }
        }

        Div(attrs = {
            classes("app-content")
        }) {
            when (val currentRoute = route) {
                is Route.NewsDetail -> {
                    NewsDetail(
                        newsId = currentRoute.id,
                        client = client,
                        onBack = { window.location.hash = "" }
                    )
                }
                is Route.NewsList -> {
                    CategoryFilter(
                        selectedCategory = selectedCategory,
                        onCategorySelected = { selectedCategory = it }
                    )

                    NewsList(
                        selectedCategory = selectedCategory,
                        client = client,
                        onFetchStateChanged = { error ->
                            latestNewsError = error
                            if (error != null) {
                                healthCheckNonce += 1
                            }
                        }
                    )
                }
            }
        }

        Footer(attrs = {
            classes("app-footer")
        }) {
            P {
                Text("© 2026 NBA ISO FLOW. All rights reserved.")
            }
        }
    }
}

object AppStylesheet : StyleSheet() {
    init {
        "*" style {
            boxSizing("border-box")
            margin(0.px)
            padding(0.px)
        }

        "body" style {
            fontFamily("'Noto Sans JP', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
            backgroundColor(Color("#0f172a"))
            color(Color("#f1f5f9"))
            lineHeight(1.6.cssRem)
        }

        ".app-container" style {
            minHeight(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }

        ".app-header" style {
            color(Color.white)
            padding(3.em, 2.em)
            textAlign("center")
            property("background", "linear-gradient(135deg, #1e40af 0%, #4f46e5 50%, #8b5cf6 100%)")
        }

        ".app-header h1" style {
            fontSize(3.5.em)
            marginBottom(0.2.em)
            fontWeight(900)
            property("letter-spacing", "-0.03em")
        }

        ".app-subtitle" style {
            fontSize(1.1.em)
            opacity(0.7)
            fontWeight(500)
            property("letter-spacing", "0.05em")
        }

        ".backend-status" style {
            property("margin", "1.5rem auto 0")
            width(100.percent)
            maxWidth(760.px)
            borderRadius(18.px)
            padding(1.em, 1.25.em)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(1.em)
            textAlign("left")
            property("backdrop-filter", "blur(8px)")
        }

        ".backend-status-checking" style {
            backgroundColor(rgba(255, 255, 255, 0.18))
            property("border", "1px solid rgba(255,255,255,0.28)")
        }

        ".backend-status-healthy" style {
            backgroundColor(rgba(15, 23, 42, 0.18))
            property("border", "1px solid rgba(191, 219, 254, 0.35)")
        }

        ".backend-status-warning" style {
            backgroundColor(rgba(120, 53, 15, 0.2))
            property("border", "1px solid rgba(253, 186, 116, 0.45)")
        }

        ".backend-status-offline" style {
            backgroundColor(rgba(127, 29, 29, 0.28))
            property("border", "1px solid rgba(252, 165, 165, 0.42)")
        }

        ".backend-status-copy" style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(0.25.em)
        }

        ".backend-status-label" style {
            fontSize(0.95.em)
            fontWeight(700)
            margin(0.px)
        }

        ".backend-status-message" style {
            fontSize(0.92.em)
            opacity(0.95)
            margin(0.px)
        }

        ".backend-status-button" style {
            property("border", "none")
            borderRadius(999.px)
            padding(0.7.em, 1.05.em)
            backgroundColor(Color.white)
            color(Color("#0f172a"))
            fontWeight(700)
            cursor("pointer")
            property("flex-shrink", "0")
        }

        ".app-content" style {
            flex(1)
            padding(2.5.em, 3.em)
            maxWidth(1400.px)
            width(100.percent)
            property("margin", "0 auto")
        }

        ".app-footer" style {
            property("border-top", "1px solid rgba(255,255,255,0.06)")
            color(Color("#64748b"))
            padding(2.em)
            textAlign("center")
            property("margin-top", "auto")
        }

        ".app-footer p" style {
            margin(0.px)
        }

        ".msw-banner" style {
            backgroundColor(Color("#f59e0b"))
            color(Color("#0f172a"))
            textAlign("center")
            padding(0.6.em, 1.em)
            fontWeight(700)
            fontSize(0.9.em)
            property("letter-spacing", "0.03em")
        }

        ".msw-indicator" style {
            position(Position.Fixed)
            property("bottom", "16px")
            property("right", "16px")
            backgroundColor(rgba(139, 92, 246, 0.85))
            color(Color.white)
            padding(0.5.em, 1.em)
            borderRadius(999.px)
            fontSize(0.8.em)
            fontWeight(700)
            property("letter-spacing", "0.05em")
            property("z-index", "9999")
            property("backdrop-filter", "blur(8px)")
            property("box-shadow", "0 2px 12px rgba(139, 92, 246, 0.4)")
        }

        media("(max-width: 700px)") {
            ".backend-status" style {
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.FlexStart)
            }

            ".backend-status-button" style {
                width(100.percent)
            }

            ".app-header h1" style {
                fontSize(2.2.em)
            }

            ".app-content" style {
                padding(1.5.em)
            }
        }
    }
}
